import {Dispatch, KeyboardEvent as ReactKeyboardEvent, SetStateAction, useState} from 'react';
import {arrayMove} from '@dnd-kit/sortable';

interface UseDndListKeyboardOptions<T> {
    data: T[];
    setData: Dispatch<SetStateAction<T[]>>;
    onToggle: (index: number) => void;
}

export function useDndListKeyboard<T>({data, setData, onToggle}: UseDndListKeyboardOptions<T>) {
    const [focusedIndex, setFocusedIndex] = useState(0);
    const [isMoving, setIsMoving] = useState(false);

    const handleListKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            const direction = event.key === 'ArrowDown' ? 1 : -1;

            if (isMoving) {
                const nextIndex = Math.min(Math.max(focusedIndex + direction, 0), data.length - 1);

                if (nextIndex !== focusedIndex) {
                    setData(arrayMove(data, focusedIndex, nextIndex));
                    setFocusedIndex(nextIndex);
                }
                return;
            }

            setFocusedIndex((index) => Math.min(Math.max(index + direction, 0), data.length - 1));
            return;
        }

        if (event.key === ' ' && event.shiftKey) {
            event.preventDefault();
            setIsMoving(true);
            return;
        }

        if (event.key === ' ' && !event.shiftKey) {
            event.preventDefault();
            onToggle(focusedIndex);
            return;
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            setIsMoving(false);
            return;
        }

        if (event.key === 'Escape') {
            setIsMoving(false);
        }
    };

    return {focusedIndex, isMoving, handleListKeyDown};
}
